// src/services/eligibilityMatch.js
//
// "Find Potential Patients" — the half that runs in the browser.
//
// The AI (functions/deriveEligibilityQuery) turns a study's inclusion/exclusion
// text into RULES over a fixed catalog of patient fields. This module does the
// rest, with no AI and no network:
//
//   1. normalizePatientForMatching()  — one patient record (Firestore consent,
//      duplicate, or legacy sheet row) -> a flat object keyed by the catalog.
//   2. evaluateRules()                — one patient x the rules -> met / failed
//      / unknown per rule.
//   3. rankCandidates()               — every patient -> the ordered shortlist.
//
// Pure and Firebase-free so it is unit-tested (eligibilityMatch.test.js) and
// the result is REPRODUCIBLE: the same rules over the same patients give the
// same list every time, which is what lets a coordinator trust it.
//
// THE CATALOG IS THE CONTRACT with functions/eligibilityQuery.js. Every key the
// server may emit in a rule must be produced here, or a rule on it evaluates
// to "unknown" for every patient — visible in the UI as a rule nobody met,
// never as a silent match.
//
// UNKNOWN IS NOT FAILED. RegX's clinical fields are sparsely filled. A patient
// with no FEV1 on record is not "not eligible", they are "unchecked on that
// criterion", and the shortlist says so. Only a rule the record actively
// contradicts rules a patient out.

import { diagnosisLabel } from "../constants/diseaseOptions";

/** The catalog keys, mirrored from functions/eligibilityQuery.js FIELD_CATALOG. */
export const MATCH_FIELDS = Object.freeze([
  "age",
  "diagnosis",
  "bmi",
  "eos",
  "exacerbations12m",
  "hospitalizedExacerbations12m",
  "fev1PrePct",
  "fev1PostPct",
  "fvcPrePct",
  "fvcPostPct",
  "fev1FvcPre",
  "fev1FvcPost",
  "reversibilityPct",
  "reversibilityMl",
  "methacholine",
  "fenoPpb",
  "oxygenLitres",
  "oxygenType",
  "cancerLast5y",
  "ahi",
  "a1c",
  "diabetes",
]);

/** Human labels for the rule list and the PDF. */
export const FIELD_LABELS = Object.freeze({
  age: "Age",
  diagnosis: "Diagnosis",
  bmi: "BMI",
  eos: "Eosinophils",
  exacerbations12m: "Exacerbations (12 mo)",
  hospitalizedExacerbations12m: "Hospitalized exacerbations (12 mo)",
  fev1PrePct: "FEV1 % pred (pre)",
  fev1PostPct: "FEV1 % pred (post)",
  fvcPrePct: "FVC % pred (pre)",
  fvcPostPct: "FVC % pred (post)",
  fev1FvcPre: "FEV1/FVC (pre)",
  fev1FvcPost: "FEV1/FVC (post)",
  reversibilityPct: "Reversibility %",
  reversibilityMl: "Reversibility mL",
  methacholine: "Methacholine",
  fenoPpb: "FeNO",
  oxygenLitres: "Oxygen L/min",
  oxygenType: "Oxygen use",
  cancerLast5y: "Cancer (5 yr)",
  ahi: "AHI",
  a1c: "A1c",
  diabetes: "Diabetes",
});

/** Patients in these pipeline states are left off the shortlist by default. */
export const DEFAULT_SKIPPED_STATUSES = Object.freeze(["Deceased", "Enrolled"]);

const MS_PER_DAY = 86400000;

// ---------------------------------------------------------------------------
// Coercion helpers — sheet data is stringly typed, Firestore is typed, and
// both have blanks and "N/A" flags. null means "not on record".
// ---------------------------------------------------------------------------

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && /\d/.test(String(v)) ? n : null;
};

const str = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

const flag = (v) => v === true || v === "true" || v === "TRUE" || v === "Yes";

/** FEV1/FVC is entered as 0.65 by some and 65 by others; rules use fractions. */
const ratio = (v) => {
  const n = num(v);
  if (n == null) return null;
  return n > 1.5 ? n / 100 : n;
};

const parseDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const ageFromDob = (dob, today = new Date()) => {
  const birth = parseDate(dob);
  if (!birth) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

const normalizeDiagnosis = (dx) => {
  const arr = Array.isArray(dx) ? dx : dx ? String(dx).split(/[,;]/) : [];
  return arr.map((d) => String(d || "").trim()).filter(Boolean);
};

/**
 * Exacerbations are a list of { date, type: "Outpatient" | "Hospital" } under
 * a heading that already says "last year". An entry with a date is counted
 * only if that date is within 12 months; an entry with no date is counted,
 * because the form's own heading is the only date it has.
 */
const countExacerbations = (list, today) => {
  if (!Array.isArray(list)) return { all: 0, hospital: 0, any: false };
  const cutoff = today.getTime() - 365 * MS_PER_DAY;
  let all = 0;
  let hospital = 0;
  let any = false;
  list.forEach((e) => {
    if (!e || typeof e !== "object") return;
    const hasContent = Boolean(str(e.date) || str(e.type));
    if (!hasContent) return;
    any = true;
    const d = parseDate(e.date);
    if (d && d.getTime() < cutoff) return;
    all += 1;
    if (/hospital/i.test(String(e.type || ""))) hospital += 1;
  });
  return { all, hospital, any };
};

/** Most recent eosinophil value, by date when dates exist, else last entered. */
const latestEos = (values) => {
  if (!Array.isArray(values)) return null;
  const withVal = values.filter((v) => v && num(v.value) != null);
  if (!withVal.length) return null;
  const dated = withVal.filter((v) => parseDate(v.date));
  if (dated.length) {
    dated.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    return num(dated[0].value);
  }
  return num(withVal[withVal.length - 1].value);
};

/**
 * Flatten one patient record into the catalog keys plus the identifiers the
 * shortlist displays. Handles all three stores: a Firestore consent, a
 * Firestore duplicate, and a legacy sheet row (whose clinical fields may still
 * be JSON in CLINICAL_DATA).
 *
 * @param {object} raw      the stored record
 * @param {"firestore"|"duplicates"|"sheet"} source
 * @param {Date} [today]    injectable for tests
 */
export const normalizePatientForMatching = (raw, source = "firestore", today = new Date()) => {
  const p = raw || {};
  let clinical = {};
  if (typeof p.CLINICAL_DATA === "string") {
    try {
      clinical = JSON.parse(p.CLINICAL_DATA) || {};
    } catch {
      clinical = {};
    }
  } else if (p.CLINICAL_DATA && typeof p.CLINICAL_DATA === "object") {
    clinical = p.CLINICAL_DATA;
  }
  // A field on the record wins over the same field inside CLINICAL_DATA.
  const f = { ...clinical, ...p };

  const pftNA = flag(f.pftNA);
  const exac = flag(f.exacNA) ? { all: 0, hospital: 0, any: true } : countExacerbations(f.exacerbations, today);

  const age = flag(f.ageNA) ? null : num(f.age) ?? ageFromDob(f.dob, today);

  return {
    id: str(p.token) || str(p.id) || "",
    source,
    name: str(f.fullName) || str(f.name) || "",
    mrn: str(f.mrn) || "",
    dob: str(f.dob) || "",
    status: str(f.status) || "",
    study: str(f.study) || "",
    studyEligibility: f.studyEligibility && typeof f.studyEligibility === "object" ? f.studyEligibility : {},

    // ---- catalog ----
    age,
    diagnosis: normalizeDiagnosis(f.diagnosis),
    bmi: num(f.bmi),
    eos: flag(f.eosNA) ? null : latestEos(f.eosValues),
    // exacNA means "none"; a form with no entries at all means "not recorded".
    exacerbations12m: flag(f.exacNA) ? 0 : exac.any ? exac.all : null,
    hospitalizedExacerbations12m: flag(f.exacNA) ? 0 : exac.any ? exac.hospital : null,
    fev1PrePct: pftNA ? null : num(f.fev1Pre),
    fev1PostPct: pftNA ? null : num(f.fev1Post),
    fvcPrePct: pftNA ? null : num(f.fvcPre),
    fvcPostPct: pftNA ? null : num(f.fvcPost),
    fev1FvcPre: pftNA ? null : ratio(f.fev1fvcPre),
    fev1FvcPost: pftNA ? null : ratio(f.fev1fvcPost),
    reversibilityPct: pftNA ? null : num(f.revPercent),
    reversibilityMl: pftNA ? null : num(f.revMl),
    methacholine: flag(f.methNA) ? null : str(f.methResult),
    fenoPpb: flag(f.fenoNA) ? null : num(f.fenoPPB),
    oxygenLitres: flag(f.oxygenNA) ? null : num(f.oxygenLitres),
    oxygenType: flag(f.oxygenNA) ? null : str(f.oxygenType),
    cancerLast5y: flag(f.cancerNA) ? null : str(f.cancerStatus),
    ahi: num(f.ahi),
    a1c: num(f.a1c),
    diabetes: str(f.diabetes),
  };
};

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

const eqi = (a, b) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/**
 * Does the patient satisfy the rule's condition? true / false / null (the
 * record doesn't say). Condition semantics only — inclusion vs exclusion is
 * applied by evaluateRules.
 */
export const testCondition = (patient, rule) => {
  if (!rule || !MATCH_FIELDS.includes(rule.field)) return null;
  const v = patient ? patient[rule.field] : undefined;
  const { op, value } = rule;

  if (rule.field === "diagnosis") {
    const list = Array.isArray(v) ? v : [];
    if (!list.length) return null;
    const has = list.some((d) => eqi(d, value));
    if (op === "includes") return has;
    if (op === "excludes") return !has;
    return null;
  }

  if (v === null || v === undefined || v === "") return null;

  if (typeof v === "number") {
    const n = Number(value);
    switch (op) {
      case "eq":
        return v === n;
      case "neq":
        return v !== n;
      case "gt":
        return v > n;
      case "gte":
        return v >= n;
      case "lt":
        return v < n;
      case "lte":
        return v <= n;
      case "between":
        return Array.isArray(value) && value.length === 2 && v >= Number(value[0]) && v <= Number(value[1]);
      default:
        return null;
    }
  }

  // enum / text
  switch (op) {
    case "eq":
      return eqi(v, value);
    case "neq":
      return !eqi(v, value);
    case "in":
      return Array.isArray(value) && value.some((x) => eqi(x, v));
    case "not_in":
      return Array.isArray(value) && !value.some((x) => eqi(x, v));
    case "contains":
      return String(v).toLowerCase().includes(String(value).toLowerCase());
    default:
      return null;
  }
};

/**
 * One patient against every rule.
 * @returns {{ met: string[], failed: string[], unknown: string[], byRule: Object<string,"met"|"failed"|"unknown"> }}
 */
export const evaluateRules = (patient, rules) => {
  const met = [];
  const failed = [];
  const unknown = [];
  const byRule = {};
  (rules || []).forEach((r) => {
    const cond = testCondition(patient, r);
    let outcome;
    if (cond === null) outcome = "unknown";
    else if (r.side === "exclusion") outcome = cond ? "failed" : "met";
    else outcome = cond ? "met" : "failed";
    byRule[r.id] = outcome;
    (outcome === "met" ? met : outcome === "failed" ? failed : unknown).push(r.id);
  });
  return { met, failed, unknown, byRule };
};

/**
 * Every patient -> the shortlist.
 *
 * A candidate is a patient no rule actively contradicts. They are ordered by
 * how much of the criteria the record confirms (most met first), then by how
 * little is unchecked, then by name so the order is stable.
 *
 * @param {object[]} patients  normalized (see normalizePatientForMatching)
 * @param {object[]} rules
 * @param {{ skipStatuses?: string[] }} [opts]
 * @returns {{ candidates: object[], ruledOut: number, skipped: number, total: number }}
 */
export const rankCandidates = (patients, rules, opts = {}) => {
  const skip = new Set((opts.skipStatuses || DEFAULT_SKIPPED_STATUSES).map((s) => s.toLowerCase()));
  const candidates = [];
  let ruledOut = 0;
  let skipped = 0;
  (patients || []).forEach((p) => {
    if (p.status && skip.has(p.status.toLowerCase())) {
      skipped += 1;
      return;
    }
    const ev = evaluateRules(p, rules);
    if (ev.failed.length) {
      ruledOut += 1;
      return;
    }
    candidates.push({ ...p, match: ev });
  });
  candidates.sort(
    (a, b) =>
      b.match.met.length - a.match.met.length ||
      a.match.unknown.length - b.match.unknown.length ||
      a.name.localeCompare(b.name)
  );
  return { candidates, ruledOut, skipped, total: (patients || []).length };
};

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

const OP_TEXT = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  includes: "includes",
  excludes: "does not include",
  in: "is one of",
  not_in: "is not",
  contains: "contains",
};

/** "Age between 40 and 80", "Diagnosis includes COPD", "Cancer (5 yr) = Yes". */
export const describeRule = (rule) => {
  if (!rule) return "";
  const label = FIELD_LABELS[rule.field] || rule.field;
  if (rule.op === "between" && Array.isArray(rule.value)) {
    return `${label} between ${rule.value[0]} and ${rule.value[1]}`;
  }
  // Diagnosis values are shown with their display label ("IPF" reads "ILD");
  // matching above still compares the stored value.
  const show = (x) => (rule.field === "diagnosis" ? diagnosisLabel(x) : x);
  const v = Array.isArray(rule.value) ? rule.value.map(show).join(", ") : String(show(rule.value));
  return `${label} ${OP_TEXT[rule.op] || rule.op} ${v}`;
};

/**
 * A stable fingerprint of the criteria text the rules were derived from, so
 * the page can tell "criteria edited since the AI last ran" without storing
 * the text twice. Not cryptographic — a change detector.
 */
export const criteriaFingerprint = (inclusion, exclusion) => {
  const s = `${String(inclusion || "").trim()} ${String(exclusion || "").trim()}`;
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  }
  return `${s.length}:${(h >>> 0).toString(16)}`; // eslint-disable-line no-bitwise
};
